import React, { useEffect, useState } from 'react';
import { Play, Pause, Plus, Minus, SkipForward, Flame, Zap, ShieldAlert, Volume2 } from 'lucide-react';
import { gymAudio } from '../../lib/audioTimer';

interface RestTimerOverlayProps {
  initialSeconds: number;
  exerciseName: string;
  nextSetNumber: number;
  onClose: () => void;
}

export const RestTimerOverlay: React.FC<RestTimerOverlayProps> = ({
  initialSeconds,
  exerciseName,
  nextSetNumber,
  onClose,
}) => {
  const [timeLeft, setTimeLeft] = useState(initialSeconds);
  const [isRunning, setIsRunning] = useState(true);

  useEffect(() => {
    if (!isRunning) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          gymAudio.playRestFinishedAlarm();
          if ('vibrate' in navigator) {
            navigator.vibrate([250, 100, 250, 100, 450]);
          }
          return 0;
        }

        const next = prev - 1;

        if (next === 3 || next === 2) {
          gymAudio.playCountdownTick();
          if ('vibrate' in navigator) {
            navigator.vibrate(80);
          }
        } else if (next === 1) {
          gymAudio.playCountdownTick();
          if ('vibrate' in navigator) {
            navigator.vibrate(120);
          }
        }

        return next;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isRunning]);

  const addTime = (secs: number) => {
    setTimeLeft((prev) => Math.max(0, prev + secs));
  };

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const isFinished = timeLeft === 0;

  const total = initialSeconds || 90;
  const progressPercent = Math.min(100, Math.max(0, ((total - timeLeft) / total) * 100));

  const radius = 100;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = (circumference) * (1 - progressPercent / 100);

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col justify-between p-6 transition-all duration-500 select-none ${
        isFinished
          ? 'bg-rose-950/95 backdrop-blur-2xl animate-pulse'
          : 'bg-dark-950/95 backdrop-blur-3xl'
      }`}
    >
      {/* 1. Header do Overlay */}
      <div className="flex items-center justify-between max-w-sm mx-auto w-full">
        <div className="flex items-center gap-2">
          <span
            className={`w-2.5 h-2.5 rounded-full ${
              isFinished ? 'bg-rose-400 animate-ping' : 'bg-brand-acid animate-ping'
            }`}
          />
          <span
            className={`text-xs font-black tracking-widest uppercase ${
              isFinished ? 'text-rose-400' : 'text-brand-acid'
            }`}
          >
            {isFinished ? 'Descanso Finalizado!' : 'Descanso Ativo Obrigatório'}
          </span>
        </div>

        <button
          onClick={onClose}
          className="px-3 py-1 rounded-xl bg-dark-900 border border-white/[0.08] text-slate-300 hover:text-white text-xs font-bold transition"
        >
          Fechar
        </button>
      </div>

      {/* 2. Centro: Anel Gigante e Cronômetro */}
      <div className="flex flex-col items-center justify-center my-auto">
        <div className="text-center mb-6">
          <span className="text-[11px] font-mono font-bold tracking-widest uppercase text-slate-400">
            {exerciseName}
          </span>
          <h2 className="text-xl font-black text-white mt-1">
            Próxima: Série #{nextSetNumber}
          </h2>
        </div>

        {/* Ring SVG Holográfico */}
        <div className="relative w-72 h-72 flex items-center justify-center">
          <svg className="w-full h-full transform -rotate-90 filter drop-shadow-[0_0_20px_rgba(163,230,53,0.3)]" viewBox="0 0 240 240">
            <defs>
              <linearGradient id="timerAcidGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ccff00" />
                <stop offset="100%" stopColor="#84cc16" />
              </linearGradient>
              <linearGradient id="timerAlertGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f43f5e" />
                <stop offset="100%" stopColor="#e11d48" />
              </linearGradient>
            </defs>

            {/* Trilha de Fundo */}
            <circle
              cx="120"
              cy="120"
              r={radius}
              stroke="#0f172a"
              strokeWidth="14"
              fill="transparent"
            />

            {/* Trilha Ativa */}
            <circle
              cx="120"
              cy="120"
              r={radius}
              stroke={isFinished ? "url(#timerAlertGradient)" : "url(#timerAcidGradient)"}
              strokeWidth="14"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              fill="transparent"
              className="transition-all duration-300"
            />
          </svg>

          {/* Numerais Digitais Gigantes */}
          <div className="absolute flex flex-col items-center">
            <span
              className={`text-7xl font-black font-mono tracking-tighter leading-none ${
                isFinished ? 'text-rose-400 animate-bounce' : 'text-white'
              }`}
            >
              {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
            </span>

            <span
              className={`text-xs font-black uppercase tracking-widest mt-2 px-3 py-0.5 rounded-full ${
                isFinished
                  ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                  : 'bg-brand-acid/15 text-brand-acid border border-brand-acid/30'
              }`}
            >
              {isFinished ? 'TEMPO ESGOTADO! TREINE!' : 'RECUPERANDO ATP'}
            </span>
          </div>
        </div>

        {/* Citação de Cobrança / Anti-Doomscrolling */}
        <div className="mt-8 px-5 py-3 rounded-2xl bg-dark-900/90 border border-white/[0.08] text-center max-w-xs shadow-card">
          <p className="text-xs text-slate-200 font-bold flex items-center justify-center gap-2">
            <Flame className="w-4 h-4 text-orange-400 shrink-0" />
            Larga as redes sociais! Respira fundo e visualiza o movimento.
          </p>
        </div>
      </div>

      {/* 3. Controles Inferiores */}
      <div className="space-y-3 max-w-sm mx-auto w-full">
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => addTime(-15)}
            className="py-3 rounded-2xl bg-dark-900 hover:bg-dark-800 border border-white/[0.08] text-xs font-mono font-black text-slate-300 active:scale-95 transition flex items-center justify-center gap-1"
          >
            <Minus className="w-3.5 h-3.5" /> 15s
          </button>

          <button
            onClick={() => setIsRunning(!isRunning)}
            className="py-3 rounded-2xl bg-dark-900 hover:bg-dark-800 border border-white/[0.08] text-xs font-black text-white active:scale-95 transition flex items-center justify-center gap-1.5"
          >
            {isRunning ? <Pause className="w-3.5 h-3.5 text-amber-400" /> : <Play className="w-3.5 h-3.5 text-lime-400" />}
            <span>{isRunning ? 'Pausar' : 'Retomar'}</span>
          </button>

          <button
            onClick={() => addTime(30)}
            className="py-3 rounded-2xl bg-dark-900 hover:bg-dark-800 border border-white/[0.08] text-xs font-mono font-black text-slate-300 active:scale-95 transition flex items-center justify-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" /> 30s
          </button>
        </div>

        {/* Botão de Pular ou Iniciar Próxima Série */}
        <button
          onClick={onClose}
          className={`w-full py-4 rounded-2xl font-black text-base uppercase tracking-wider transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2 cursor-pointer ${
            isFinished
              ? 'bg-rose-500 hover:bg-rose-400 text-white shadow-glow-danger'
              : 'bg-gradient-to-r from-brand-acid to-lime-400 hover:from-lime-300 hover:to-emerald-400 text-slate-950 shadow-glow-lime'
          }`}
        >
          <SkipForward className="w-5 h-5" />
          <span>{isFinished ? 'INICIAR SÉRIE AGORA' : 'Pular Descanso & Treinar'}</span>
        </button>
      </div>
    </div>
  );
};
